import { useRef } from 'react';

import { useWalletAccess } from '@/features/wallet-access/wallet-access-provider';
import { SendStatus } from '@/shared/modules/wallet/send/send-state';
import { type SendInput } from '@/shared/modules/wallet/send/send-transfer';
import { useSend } from '@/shared/modules/wallet/send/use-send';

const BUSY_STATUSES = new Set([
  SendStatus.PREPARING,
  SendStatus.AWAITING_SIGNATURE,
  SendStatus.SUBMITTING,
]);

export function useSendSubmission() {
  const { recordActivity, unlock } = useWalletAccess();
  const { send, reset, checkStatus, status, error, digest, requiresReauthentication } = useSend();
  const cancellation = useVerificationCancellation(reset);

  const submit = (input: SendInput) => {
    if (recordActivity()) {
      cancellation.track(send(input));
    }
  };

  const reauthenticate = async () => {
    if (await unlock()) {
      reset();
    }
  };

  return {
    state: {
      status,
      error,
      digest,
      requiresReauthentication,
      isBusy: BUSY_STATUSES.has(status),
    },
    submit,
    reset,
    restart: cancellation.restart,
    checkPendingTransfer: () => void checkStatus().catch(() => undefined),
    reauthenticate,
    cancelVerification: cancellation.markCancelled,
  };
}

function useVerificationCancellation(reset: () => void) {
  const cancelled = useRef(false);

  const track = (sending: Promise<unknown>) => {
    cancelled.current = false;
    void sending.catch(() => {
      if (cancelled.current) {
        cancelled.current = false;
        reset();
      }
    });
  };

  const restart = () => {
    cancelled.current = false;
    reset();
  };

  const markCancelled = () => {
    cancelled.current = true;
  };

  return { track, restart, markCancelled };
}
